import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { onAuthStateChanged, onIdTokenChanged, type User } from "firebase/auth";
import { auth } from "./firebase";

export type Role = "platform_admin" | "tenant_admin" | "ambassador" | "customer" | "staff";

export type AuthClaims = {
  tenantId: string | null;
  role: Role | null;
  ambassadorId: string | null;
};

type AuthState = {
  user: User | null;
  claims: AuthClaims;
  loading: boolean;
  refreshClaims: () => Promise<void>;
};

const emptyClaims: AuthClaims = { tenantId: null, role: null, ambassadorId: null };

const AuthContext = createContext<AuthState>({
  user: null,
  claims: emptyClaims,
  loading: true,
  refreshClaims: async () => {}
});

async function readClaims(user: User | null): Promise<AuthClaims> {
  if (!user) return emptyClaims;
  const token = await user.getIdTokenResult();
  return {
    tenantId: (token.claims.tenantId as string | undefined) ?? null,
    role: (token.claims.role as Role | undefined) ?? null,
    ambassadorId: (token.claims.ambassadorId as string | undefined) ?? null
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<AuthClaims>(emptyClaims);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      setClaims(await readClaims(u));
      setLoading(false);
    });
    const unsubToken = onIdTokenChanged(auth, async (u) => {
      if (u) setClaims(await readClaims(u));
    });
    return () => {
      unsubAuth();
      unsubToken();
    };
  }, []);

  const refreshClaims = async () => {
    if (!auth.currentUser) return;
    await auth.currentUser.getIdToken(true);
    setClaims(await readClaims(auth.currentUser));
  };

  return (
    <AuthContext.Provider value={{ user, claims, loading, refreshClaims }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
